import { useFormik } from "formik";
import { useAlert } from "hooks/useAlert";
import { useAuth } from "hooks/useAuth";
import { useSession } from "hooks/useSession";
import { useToast } from "hooks/useToast";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router";
import { checkChangeDeep } from "utils/checkChange";
import { noDdiPhone } from "utils/regex";
import * as Yup from 'yup'
import SellerDetailsContext from "..";

interface ParamsProps {
  id: string;
}

type Department = 'Comercial' | 'SAC' | 'Financeiro' | 'Jurídico' | 'TI'

interface SupplierDepartmentContact {
  department: Department,
  responsible?: string,
  email?: string,
  phoneNumber?: string,
  branch?: number,
}

export interface ResponsibleInputsProps {
  responsible?: string,
  email?: string,
  phoneNumber?: string,
  branch?: string,
}

interface EncryptedFieldProps {
  email: {
    isEncrypted: boolean,
    isLoading: boolean,
  },
  phoneNumber: {
    isEncrypted: boolean,
    isLoading: boolean,
  }
}

interface InputsValuesProps {
  commercial: ResponsibleInputsProps,
  sac: ResponsibleInputsProps,
  finances: ResponsibleInputsProps,
  legal: ResponsibleInputsProps,
  it: ResponsibleInputsProps,
}

export interface EncryptedFieldsProps {
  commercial: EncryptedFieldProps,
  sac: EncryptedFieldProps,
  finances: EncryptedFieldProps,
  legal: EncryptedFieldProps,
  it: EncryptedFieldProps
}

const initInputsValues: InputsValuesProps = {
  commercial: {
    responsible: '',
    email: '',
    phoneNumber: '',
    branch: '',
  },
  sac: {},
  finances: {},
  legal: {},
  it: {},
}

const initEncryptedField: EncryptedFieldProps = {
  email: {
    isEncrypted: true,
    isLoading: false,
  },
  phoneNumber: {
    isEncrypted: true,
    isLoading: false,
  }
}

const initEncryptedFields: EncryptedFieldsProps = {
  commercial: initEncryptedField,
  sac: initEncryptedField,
  finances: initEncryptedField,
  legal: initEncryptedField,
  it: initEncryptedField
}

const translatedFields = (props: string) => (
  {
    "commercial": "Responsável Comercial",
    "sac": "Responsável pelo SAC",
    "finances": "Responsável Financeiro",
    "legal": "Responsável Jurídico",
    "it": "Responsável TI",
  }
)[props];

const departmentFieldToType = (props: string):Department => (
  {
    'commercial': 'Comercial' as Department,
    'sac': 'SAC' as Department,
    'finances': 'Financeiro' as Department,
    'legal': 'Jurídico' as Department,
    'it': 'TI' as Department,
  }
)[props] ?? 'Comercial';

export const useDepartmentContacts = () => {
  const { id } = useParams<ParamsProps>();
  const [initialInputsValues, setInitialInputsValues] = useState<InputsValuesProps>(initInputsValues);
  const [encryptedFields, setEncryptedFields] = useState<EncryptedFieldsProps>(initEncryptedFields);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [changedFields, setChangedFields] = useState<string[]>([]);
  const { setToast } = useToast();
  const {getSessionAuthorization} = useSession();
  const { setHasChangedFields } = useContext(SellerDetailsContext);
  const { alert, setTextAlertInterface, openAlert, closeAlert, caller } = useAlert();
  const { connectApi } = useAuth();
  const {handleSubmit, handleChange, setValues: setInputsValues, values: inputsValues, touched, errors} = useFormik({
    initialValues: initInputsValues,
    validationSchema: Yup.object({
      commercial: Yup.object({
        responsible: Yup.string().required(),
        email: Yup.lazy(_ => yupEmailValidation('commercial')),
        phoneNumber: Yup.lazy(_ => yupPhoneNumberValidation('commercial')),
        branch: Yup.number(),
      }),
      sac: Yup.lazy(value => {
        if (value && Object.keys(value).length > 0) {
          return Yup.object({
            responsible: Yup.string().required(),
            email: Yup.lazy(_ => yupEmailValidation('sac')),
            phoneNumber: Yup.lazy(_ => yupPhoneNumberValidation('sac')),
          })
        }
        return Yup.mixed().notRequired();
      }),
      finances: Yup.lazy(value => {
        if (value && Object.keys(value).length > 0) {
          return Yup.object({
            responsible: Yup.string().required(),
            email: Yup.lazy(_ => yupEmailValidation('finances')),
            phoneNumber: Yup.lazy(_ => yupPhoneNumberValidation('finances')),
          })
        }
        return Yup.mixed().notRequired();
      }),
      legal: Yup.lazy(value => {
        if (value && Object.keys(value).length > 0) {
          return Yup.object({
            responsible: Yup.string().required(),
            email: Yup.lazy(_ => yupEmailValidation('legal')),
            phoneNumber: Yup.lazy(_ => yupPhoneNumberValidation('legal')),
          })
        }
        return Yup.mixed().notRequired();
      }),
      it: Yup.lazy(value => {
        if (value && Object.keys(value).length > 0) {
          return Yup.object({
            responsible: Yup.string().required(),
            email: Yup.lazy(_ => yupEmailValidation('it')),
            phoneNumber: Yup.lazy(_ => yupPhoneNumberValidation('it')),
          })
        }
        return Yup.mixed().notRequired();
      }),
    }),
    onSubmit: (values) => sendData(values)
  });

  const yupPhoneNumberValidation = (department: keyof typeof encryptedFields) => {
    if (encryptedFields[department].phoneNumber.isEncrypted ?? true)
      return Yup.string().required();
    return Yup.string().matches(noDdiPhone).required();
  }

  const yupEmailValidation = (department: keyof typeof encryptedFields) => {
    if (encryptedFields[department].email.isEncrypted ?? true)
      return Yup.string().required();
    return Yup.string().email().required();
  }

  /* HOOKS */
  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (caller === 'departmentContacts') {
      alert.typeContinue === "save" && saveFormChanges();
      alert.typeContinue === "discard" && discardChanges();
    }
  }, [alert])

  useEffect(() => {
    const changed = checkChangeDeep(inputsValues, initialInputsValues);
    setChangedFields(changed);
    setHasChangedFields(changed.length > 0)
  }, [inputsValues])

  /* FORM SUBMISSION HANDLERS */
  const saveFormChanges = () => {
    handleSubmit();
    if (Object.keys(errors).length > 0) {
      setToast({
        message: `Favor corrigir o(s) campo(s) indicado(s)`,
        isActive: true,
        type: 'error',
        timeToRemoveToast: 3000
      })
      closeAlert();
      return;
    }
  }
  
  const discardChanges = () => {
    setInputsValues(initialInputsValues);
    setEncryptedFields(initEncryptedFields);
    closeAlert();
  }


  /* EVENT HANDLERS */
  const handleAddResponsible = (event: any) => {
    const targetName = event.target.name;
    setInputsValues({
      ...inputsValues,
      [targetName]: {
        responsible: '',
        email: '',
        phoneNumber: '',
      }
    });
    setEncryptedFields({
      ...encryptedFields,
      [targetName]: {
        email: {
          isEncrypted: false,
          isLoading: false,
        },
        phoneNumber: {
          isEncrypted: false,
          isLoading: false,
        }
      }
    })
  }

  const handleRemoveResponsible = (event: any) => {
    const targetName = event.target.name;
    setInputsValues({
      ...inputsValues,
      [targetName]: {},
    })
  }

  const handleFormDiscard = () => {
    showAlertForm(false)
  }

  const handleFormSubmit = () => {
    showAlertForm(true)
  }

  const handleDecrypt = async (departmentField: string, field: 'phoneNumber' | 'email') => {
    const department = departmentFieldToType(departmentField);
    setEncryptedFields({
      ...encryptedFields,
      [departmentField]: {
        ...encryptedFields[departmentField as keyof typeof encryptedFields],
        [field]: {
          ...encryptedFields[departmentField as keyof typeof encryptedFields][field],
          isLoading: true,
        }
      }
    })
    connectApi({
      type: 'get',
      url: `sellers/${id}/unmask?department=${department}&field=${field}`,
    }).then((response) => {
      const responseDecoded = response.data;
      const decryptedValue = responseDecoded[field];
      setEncryptedFields({
        ...encryptedFields,
        [departmentField]: {
          ...encryptedFields[departmentField as keyof typeof encryptedFields],
          [field]: {
            isEncrypted: false,
            isLoading: false,
          }
        }
      });
      setInputsValues({
        ...inputsValues,
        [departmentField]: {
          ...inputsValues[departmentField as keyof typeof inputsValues],
          [field]: decryptedValue,
        }
      })
    }).catch((_) => {
      setEncryptedFields({
        ...encryptedFields,
        [departmentField]: {
          ...encryptedFields[departmentField as keyof typeof encryptedFields],
          [field]: {
            isEncrypted: true,
            isLoading: false,
          }
        }
      })
    })
  }

  /* HELPERS */
  const showAlertForm = (typeOpen: boolean) => {
    setTextAlertInterface({
      positionCenter: false,
    })
    openAlert(typeOpen, 'departmentContacts');
  }

  /* API CALLS */
  const getData = async () => {

    setIsLoading(true);
    connectApi({
      type: 'get',
      url: `sellers/${id}/department-contact`,
    }).then((response) => {
      setHasError(false);
      const responseDecoded: SupplierDepartmentContact[] = response.data;
      const commercialContact = responseDecoded.find((contact) => contact.department == 'Comercial');
      const sacContact = responseDecoded.find((contact) => contact.department == 'SAC');
      const financesContact = responseDecoded.find((contact) => contact.department == 'Financeiro');
      const legalContact = responseDecoded.find((contact) => contact.department == 'Jurídico');
      const itContact = responseDecoded.find((contact) => contact.department == 'TI');

      const values:InputsValuesProps = {
        commercial: {
          responsible: commercialContact?.responsible ?? '',
          email: commercialContact?.email ?? '',
          phoneNumber: commercialContact?.phoneNumber?.replaceAll('+55', '') ?? '',
          branch: commercialContact?.branch?.toString() ?? '',
        },
        sac: sacContact ? {
          responsible: sacContact.responsible,
          email: sacContact.email,
          phoneNumber: sacContact.phoneNumber?.replaceAll('+55', ''),
        } : {},
        finances: financesContact ? {
          responsible: financesContact.responsible,
          email: financesContact.email,
          phoneNumber: financesContact.phoneNumber?.replaceAll('+55', ''),
        } : {},
        legal: legalContact ? {
          responsible: legalContact.responsible,
          email: legalContact.email,
          phoneNumber: legalContact.phoneNumber?.replaceAll('+55', ''),
        } : {},
        it: itContact ? {
          responsible: itContact.responsible,
          email: itContact.email,
          phoneNumber: itContact.phoneNumber?.replaceAll('+55', ''),
        } : {},
      }
      setEncryptedFields(initEncryptedFields);
      setInitialInputsValues(values);
      setInputsValues(values);
    }).catch((_) => {
      setHasError(true);
    }).finally(() => {
      setIsLoading(false);
    })
  }

  const sendData = async(values:InputsValuesProps) => {
    let body = Object.keys(values).flatMap((key: string) => {
      let value = values[key as keyof typeof values];
      if (Object.keys(value).length > 0) {
        return {
          department: departmentFieldToType(key),
          responsible: value.responsible,
          email: value.email,
          phoneNumber: value.phoneNumber,
          ...(value.branch && value.branch !== '' && {branch: value.branch})
        }
      } else {
        return []
      }
    })

    connectApi({
      type: 'put',
      url: `sellers/${id}/department-contact`,
      body: body,
      successMessage: `Seller atualizado com sucesso`,
    }).then((_) => {
      getData();
      closeAlert();
    }).catch((_) => {
      closeAlert();
    })
  }

  return {
    isLoading,
    hasError,
    inputsValues,
    initialInputsValues,
    errors,
    touched,
    changedFields,
    encryptedFields,
    translatedFields,
    handleChange,
    handleSubmit,
    handleAddResponsible,
    handleRemoveResponsible,
    handleFormSubmit,
    handleFormDiscard,
    handleDecrypt,
  }
}